import React, { useState, useEffect } from "react";

import {getLog, postLog} from '../helpers/log';

// MemberLogin component (used to log in to membership dashboard)
const MemberLogin = (props) => {
    const {
        config,
        authenticationToken,
        isMember, setIsMember,
        memberInfo,
        setMemberInfo,
        setMemberOptions,
        checkoutObject,
        setCheckoutObject,
        setActiveScreen,
        refetchingMemberData,
        setRefetchingMemberData
    } = props;
    
    const [memberCredentials, setMemberCredentials] = useState({memberId: '', lastName: ''});
    const [isFetching, setIsFetching] = useState(false);
    const [hasError, setHasError] = useState(false);

    // "memberId": "2443698",
    // "lastName": "Test"

    useEffect(() => {
        if (refetchingMemberData === true) {
            setMemberInfo(null);
            setMemberOptions(null);
            fetchMemberData(memberCredentials.memberId, memberCredentials.lastName);
            setRefetchingMemberData(false);
        }
    }, [refetchingMemberData]);

    const fetchMemberData = async (id, ln) => {
        setIsFetching(true);
        try {
            const response = await fetch(`https://${config.apiUrl}/api/membership/check`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                },
                body: JSON.stringify({
                    "memberId": id,
                    "lastName": ln
                })
            });
            if (!response.ok) {
                postLog(config, authenticationToken, response.url, String(response.status));
                throw new Error(response.url + ' ' + response.status);
            }
            const data = await response.json();
            console.log(data.data);
            if (data.data.primaryCardHolder != null) {
                setHasError(false);
                // country check for dropdown
                let country = data.data.address.country;
                if (country == 'United States' || country == 'United States of America' || country == 'USA') {
                    country = 'US';
                } else if (country == 'Canada') {
                    country = 'CA';
                }
                setCheckoutObject({
                    ...checkoutObject,
                    'billingFirstName': data.data.primaryCardHolder.firstName,
                    'billingLastName': data.data.primaryCardHolder.lastName,
                    'billingAddress1': data.data.address.streetAddress1,
                    'billingCity': data.data.address.city,
                    'billingState': data.data.address.state,
                    'billingCountry': country,
                    'billingZipCode': data.data.address.zipCode,
                    'billingEmail': data.data.primaryCardHolder.email,
                    'billingPhoneNumber': data.data.primaryCardHolder.phoneNumber,
                    'manualEntryCardNumber': null,
                    'expDate': null,
                    'cvc': null
                });
                setMemberInfo(data.data);
                setIsMember(true);
                setActiveScreen("dashboard");
                window.scrollTo(0, 200);
                fetchMemberOptions(data.data.primaryCardHolder.membershipId, data.data);
            } else {
                setHasError(true);
                console.error(data.data);
            }
        } catch (error) {
            console.error(error);
        }
        setIsFetching(false);
    }

    const fetchMembershipLevel = async (membershipLevelId) => {
        try {
            const response = await fetch(`https://${config.apiUrl}/api/membership/levels/${membershipLevelId}`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                }
            });
            if (!response.ok) {
                postLog(config, authenticationToken, response.url, String(response.status));
                throw new Error(response.url + ' ' + response.statusText);
            }
            const data = await response.json();
            if (data?.data) {
                return data.data;
            } else {
                setHasError(true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchMemberOptions = async (membershipId, memberInfoOverride = null) => {
        try {
            const response = await fetch(`https://${config.apiUrl}/api/membership/${membershipId}/eligible-upgrade-options`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                }
            });
            if (!response.ok) {
                postLog(config, authenticationToken, response.url, String(response.status));
                throw new Error(response.url + ' ' + response.statusText);
            }
            const data = await response.json();
            if (data.data) {
                if(!data.data.current) {
                    const currentMemberInfo = memberInfoOverride || memberInfo;
                    if(currentMemberInfo?.primaryCardHolder?.categoryId) {
                        data.data.current = await fetchMembershipLevel(currentMemberInfo.primaryCardHolder.categoryId);
                    }
                }
                setMemberOptions(data.data);
            } else {
                setHasError(true);
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div className="membership__login-form invert">
            <label htmlFor="login-membership-number" className="sr-only">Membership Number</label>
            <input
                id="login-membership-number"
                className={`${hasError ? 'error' : ''} ${isMember ? 'success' : ''}`}
                type="text"
                placeholder="Membership Number"
                disabled={!authenticationToken}
                onChange={(e) => setMemberCredentials(memberCredentials => ({...memberCredentials, memberId: e.target.value}))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && memberCredentials.memberId.length > 0 && memberCredentials.lastName.length > 0) {
                        fetchMemberData(memberCredentials.memberId, memberCredentials.lastName);
                    }
                }}
            />
            <label htmlFor="login-last-name" className="sr-only">Last Name</label>
            <input
                id="login-last-name"
                className={`${hasError ? 'error' : ''} ${isMember ? 'success' : ''}`}
                type="text"
                placeholder="Last Name"
                disabled={!authenticationToken}
                onChange={(e) => setMemberCredentials(memberCredentials => ({...memberCredentials, lastName: e.target.value}))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && memberCredentials.memberId.length > 0 && memberCredentials.lastName.length > 0) {
                        fetchMemberData(memberCredentials.memberId, memberCredentials.lastName);
                    }
                }}
            />
            {hasError && <p>Member information not found.</p>}
            <button
                className="button"
                onClick={() => fetchMemberData(memberCredentials.memberId, memberCredentials.lastName)}
                disabled={memberCredentials.memberId.length < 1 || memberCredentials.lastName.length < 1 || isFetching}
            >
                {!isFetching ? 'Sign In' : <i className="fa fa-spinner fa-pulse"></i>}
            </button>
            <br/><p><a href="mailto:membership@seattleaquarium.org">Forgot Member Number?</a></p>
        </div>
    )
}

export default MemberLogin;